import '../../common/EsCommon.css';
import './EsAccount.css';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { API_ACCOUNT, API_ACCOUNT_AVATAR, API_ACCOUNT_DELETE, API_ACCOUNT_FORKED_PACKS, API_ACCOUNT_OWN, API_ACCOUNT_PASSWORD, TAB_CLIENTS, TAB_DEVICES, TAB_PASSWORD, TAB_PERSONAL, TAB_SUBSCRIPTION, VIEW_REGISTER } from '../../common/EsConstants';
import { esDelete, esLeaveAfterAccountDelete, esRead, esPost, esPut, esUpload } from '../../common/EsFetch';
import { serverUrl } from '../../common/EsEnv';
import { esAccountFaceRev, esAccountFaceSet } from '../../common/EsAccountFace';
import EsConfirmDelete from '../../common/EsConfirmDelete';
import EsForkedPacks, { esReadForkedPacks } from './EsForkedPacks';
import { useScrollIntoViewWhen } from '../../common/EsScrollIntoView';
import { reservedNicknameRefusal } from '../../common/EsNicknames';
import { useEsAiFlag, useEsTranslationsFlag, useEsCrafterFlag, useEsUnrealPluginFlag, useEsVoiceFlag } from '../../common/EsFlags';
import { EsFileUpload, EsTabs } from '../../common/EsPrime';
import EsAccountSub from './EsAccountSub';
import EsAccountClients from './EsAccountClients';
import EsAccountDevices from './EsAccountDevices';
import EsAccountBridge from './EsAccountBridge';
import EsLocalePicker from './EsLocalePicker';
import EsTranslateOffer from './EsTranslateOffer';
import EsAccountVoice from './EsAccountVoice';
import EsAccountAi from './EsAccountAi';
import EsAiMeter from '../../common/EsAiMeter';
import { useEsStrings } from '../../common/EsStrings';
import EsMinis from '../../common/EsMinis';

/**
 * Your own account: the name people see, and your password.
 *
 * THE MARKUP IS EsRegister's AND EsLogin's, and deliberately. The three are the
 * same kind of page -- a centred card on the marketing section shell -- and a
 * person arriving here from registration should recognise where they are. The
 * classes are copied rather than abstracted because that is how those two are
 * written; extracting a shared form component is a change to all three and is
 * not this one.
 *
 * THE FETCHING IS NOT EsRegister's. Those two hand-roll the four headers, the
 * text()-then-parse dance and the status check inline, and EsFetch.js was
 * written afterwards precisely to stop that being copied a thirtieth time --
 * esRead and esWrite carry the XSRF token, throw on a non-2xx, and surface
 * esback's {"msg": ...} as the Error's message. Every refusal this screen can
 * receive is one of those envelopes (403 for somebody else's account, 403 for a
 * wrong current password, 400 for a password the Cognito pool refuses), so the
 * catch below is where all three arrive already spelled as sentences.
 *
 * TWO FORMS, TWO MESSAGE LINES, and not one of each. They fail independently
 * and for unrelated reasons; a single status line means saving a nickname wipes
 * the sentence telling you why your password was refused, which is the one you
 * were still reading.
 *
 * WHAT IS NOT HERE. A theme. es_config's theme is an ENVIRONMENT setting an
 * admin owns -- EsConfigService, EsThemeMenu, and the PUT is ROLE_ADMIN -- so
 * there is no per-account preference to wire, and inventing one is a schema
 * change plus a decision nobody has made. It is raised in the handover instead.
 */
export default function EsAccount() {
    const t = useEsStrings();
    // The games signed in as this account (es-unreal-plugin.md §4.2).
    const devicesOn = useEsUnrealPluginFlag();
    // The language choice needs something to choose between: copykeys, or Crafter's pages.
    const crafterOn = useEsCrafterFlag();
    const copykeysOn = useEsTranslationsFlag();
    const voiceOn = useEsVoiceFlag();
    const aiOn = useEsAiFlag();
    const localeOn = crafterOn || copykeysOn;
    const [ search, setSearch ] = useSearchParams();

    const [account, setAccount] = useState( null );
    const [nickname, setNickname] = useState( '' );
    /**
     * Whether this person has asked for no Escapade mailings.
     *
     * <p>Held separately from `account` like the nickname is, because it is a
     * control somebody is editing rather than the stored value. Unlike the
     * nickname it is seeded UNCONDITIONALLY from the response: a checkbox has
     * no "not typed into yet" state to protect -- false before the GET answers
     * and false after it are the same rendering -- and the value may have been
     * set from somewhere else entirely, by the link at the foot of a mailing.
     * Somebody who opted out by email must not open this page and be told
     * they are still on the list.
     */
    const [optOut, setOptOut] = useState( false );
    // Initials ride in the form's PUT, as the nickname does; the avatar is bytes and goes the
    // moment it is chosen.
    const [ initials, setInitials ] = useState( '' );
    const [ avatarBusy, setAvatarBusy ] = useState( false );
    const [ avatarNote, setAvatarNote ] = useState( '' );
    const [ avatarConfirming, setAvatarConfirming ] = useState( false );
    const [accountError, setAccountError] = useState( null );
    // Authenticated, but there is no es_account row behind the session. Its own
    // state rather than a accountError, because it is not an error and does not
    // render as one -- see the catch below and the early return before the form.
    const [notFound, setNotFound] = useState( false );
    const accountErrorRef = useScrollIntoViewWhen( Boolean( accountError ) );
    const [accountSaved, setAccountSaved] = useState( null );

    // Deleting the account: the dialog, what was typed into it, and the server's refusal.
    const [ deleteAsking, setDeleteAsking ] = useState( false );
    const [ deleteTyped, setDeleteTyped ] = useState( '' );
    const [ deleteBusy, setDeleteBusy ] = useState( false );
    const [ deleteError, setDeleteError ] = useState( null );
    // The packs others built on theirs (V126): asked about before the typed confirm, and only when
    // there are some. hideForked is the yes, sent with the delete.
    const [ forked, setForked ] = useState( [] );
    const [ forkedAsking, setForkedAsking ] = useState( false );
    const [ hideForked, setHideForked ] = useState( false );

    /**
     * Whether anything on this form differs from the row the server last gave us.
     *
     * <p>Derived, not tracked. A boolean flag set by every onChange is one
     * handler away from lying -- and it would still say "dirty" after somebody
     * typed a character and deleted it again. Comparing the values answers the
     * question the button is actually asking: is there anything to save.
     *
     * <p>The nickname compares against the STORED value and not the display
     * name, because an empty box means "go back to the default" and the server
     * stores the address's local part for it -- so a blank box over a stored
     * blank is clean, and a blank box over a stored name is not.
     */
    const dirty = Boolean( account ) && (
        ( nickname || '' ) !== ( account.nickname || '' )
        || optOut !== Boolean( account.isOptOut )
        || ( initials || '' ) !== ( account.initials || '' ) );

    const blankPasswords = { currentPassword: '', newPassword: '', checkPassword: '' };
    const [passwords, setPasswords] = useState( blankPasswords );
    const [passwordError, setPasswordError] = useState( null );
    const passwordErrorRef = useScrollIntoViewWhen( Boolean( passwordError ) );
    const [passwordSaved, setPasswordSaved] = useState( null );

    // The id is not known until this answers. /api/v1/authUser carries a name
    // and the Cognito groups and never the row, so the bare GET below is the
    // only way in -- and it is why every write here waits for `account`.
    useEffect( () => {
        esRead( API_ACCOUNT )
            .then( aBody => {
                setAccount( aBody );
                // '' and not the displayName. The box holds what is STORED, so
                // an account that never chose a nickname shows an empty field
                // with the fallback as its placeholder -- seeding it with the
                // derived name would make "I never chose" indistinguishable
                // from "I chose exactly my own local part", and would store the
                // second the first time anybody pressed Save.
                //
                // SEEDS an empty box; never overwrites one somebody has typed
                // into. This box is rendered and editable before the GET above
                // answers, so a person who starts typing straight away had
                // their words replaced when it did -- and the Save button is
                // disabled until `account` arrives, which guarantees the wipe
                // lands BEFORE they can submit. What they then saved was the
                // empty box, which the server stores as the address's local
                // part: type quickly and your new nickname became your old one,
                // silently. AQA caught it as "A nickname survives leaving the
                // page and coming back" failing only on a loaded machine.
                setNickname( ( pTyped ) => pTyped || aBody?.nickname || '' );
                setOptOut( Boolean( aBody?.isOptOut ) );
                setInitials( ( pTyped ) => pTyped || aBody?.initials || '' );
            } )
            .catch( aError => {
                // 404 IS NOT AN ERROR ON THIS SCREEN, it is a state. The caller
                // is authenticated -- EsAuthConfig saw to that before this
                // request reached a controller -- and esback answers 404 to say
                // "you have no row", which is a different fact from "who are
                // you". See EsAccountController.ownAccount, which chose that
                // status deliberately and says why.
                //
                // It happens: a Cognito user with no es_account row, which is
                // what a half-finished registration leaves and what the pets
                // fixtures are. Found in prod on 2026-09-17 -- the page drew
                // its whole form over an empty account, offered a Save that
                // could only fail, and put "/api/v1/account answered 404" on
                // the screen in place of a sentence.
                if ( aError.status === 404 ) {
                    setNotFound( true );
                    return;
                }

                setAccountError( aError.message );
            } );
    }, [] );

    const uploadAvatar = ( pFile ) => {
        const aForm = new FormData();
        aForm.append( 'file', pFile );
        setAvatarBusy( true );
        setAvatarNote( '' );

        esUpload( API_ACCOUNT_AVATAR, aForm )
            .then( ( pAnswer ) => {
                setAccount( ( pPrevious ) => ( { ...pPrevious, avatarRev: pAnswer.avatarRev, pictureUrl: null } ) );
                esAccountFaceRev( pAnswer.avatarRev );
            } )
            .catch( ( pError ) => setAvatarNote( pError.message ) )
            .finally( () => setAvatarBusy( false ) );
    };

    const removeAvatar = () => {
        setAvatarConfirming( false );
        setAvatarBusy( true );
        setAvatarNote( '' );

        esDelete( API_ACCOUNT_AVATAR )
            // Read back rather than assumed: with the upload gone esback may send the Google
            // picture in its place, and the screen must not invent which.
            .then( () => esRead( API_ACCOUNT ) )
            .then( ( pView ) => {
                setAccount( ( pPrevious ) => ( { ...pPrevious, avatarRev: pView.avatarRev, pictureUrl: pView.pictureUrl } ) );
                esAccountFaceSet( pView );
            } )
            .catch( ( pError ) => setAvatarNote( pError.message ) )
            .finally( () => setAvatarBusy( false ) );
    };

    // The packs step first when there are packs to ask about, else straight to the typed confirm.
    const askToDelete = async () => {
        setDeleteError( null );
        setDeleteTyped( '' );
        setHideForked( false );
        const aForked = await esReadForkedPacks( API_ACCOUNT_FORKED_PACKS );
        setForked( aForked );
        if ( aForked.length > 0 ) setForkedAsking( true );
        else setDeleteAsking( true );
    };

    const deleteAnyway = () => {
        setForkedAsking( false );
        setHideForked( true );
        setDeleteAsking( true );
    };

    // The typed address goes to esback, which checks it again against the session's own row.
    // A success has ended the session there; the page leaves for home, signed out. A 409 to a
    // delete sent without the yes may be a pack somebody built on since the dialog opened: asked
    // again, as the button asks. With the yes, a 409 is another refusal (the last administrator).
    const deleteAccount = async () => {
        setDeleteBusy( true );
        setDeleteError( null );
        try {
            await esPost( API_ACCOUNT_DELETE, hideForked ? { email: deleteTyped, hideForked: true } : { email: deleteTyped } );
            esLeaveAfterAccountDelete();
        }
        catch ( aError ) {
            setDeleteAsking( false );
            setDeleteBusy( false );
            const aForked = aError.status === 409 && !hideForked ? await esReadForkedPacks( API_ACCOUNT_FORKED_PACKS ) : [];
            if ( aForked.length > 0 ) {
                setForked( aForked );
                setHideForked( false );
                setForkedAsking( true );
            }
            else setDeleteError( aError.message );
        }
    };

    const handlePasswordChange = ( event ) => {
        const { name, value } = event.target;
        setPasswords( { ...passwords, [ name ]: value } );
    };

    const [ nicknameError, setNicknameError ] = useState( null );

    const handleAccountSubmit = async ( event ) => {
        event.preventDefault();
        setAccountError( null );
        setAccountSaved( null );

        const aRefusal = await reservedNicknameRefusal( nickname );
        setNicknameError( aRefusal );
        if ( aRefusal ) return;

        try {
            // The ANSWER is what goes into state, never the value that was
            // typed. A blank nickname is saved as the address's local part
            // rather than refused, so the server's copy of this row and the box
            // on this screen disagree the moment somebody clears it -- and the
            // server's is the true one. EsConfigController makes the same point
            // about a theme setting that was cleared.
            const aSaved = await esPut( API_ACCOUNT_OWN + '/' + account.id,
                { nickname, isOptOut: optOut, initials } );
            setAccount( aSaved );
            setNickname( aSaved?.nickname || '' );
            setOptOut( Boolean( aSaved?.isOptOut ) );
            setInitials( aSaved?.initials || '' );
            esAccountFaceSet( aSaved );
            setAccountSaved( 'Saved.' );
        }
        catch ( aError ) {
            setAccountError( aError.message );
        }
    };

    const handlePasswordSubmit = async ( event ) => {
        event.preventDefault();
        setPasswordError( null );
        setPasswordSaved( null );

        // Checked here as well as by the pool, because this is the one rule
        // Cognito cannot check: it never sees the second box. The LENGTH and
        // the character classes are deliberately NOT restated -- the policy
        // lives in the pool and EsAuthService hands back its own sentence
        // saying which part was broken, and a copy of it here could only drift.
        // Except when there is not one. esback asks Cognito the same question and
        // is the side that decides it; this is only so the form does not demand
        // something the box above will not let anybody type.
        if ( !aGoogleOnly && !passwords.currentPassword ) {
            setPasswordError( t( 'account-settings-password-current-required', 'Your current password is required.' ) );
            return;
        }
        if ( !passwords.newPassword ) {
            setPasswordError( t( 'account-settings-password-new-required', 'A new password is required.' ) );
            return;
        }
        if ( passwords.newPassword !== passwords.checkPassword ) {
            setPasswordError( t( 'account-settings-password-mismatch', 'New Password and Check Password do not match.' ) );
            return;
        }

        try {
            const aSaved = await esPost( API_ACCOUNT_PASSWORD, {
                currentPassword: passwords.currentPassword,
                newPassword: passwords.newPassword,
            } );

            // Cleared on success, and this is not only tidiness: the three
            // boxes are holding two live passwords, and leaving them in React
            // state keeps them in the page for as long as the tab is open.
            setPasswords( blankPasswords );

            // ⚠️ THE ANSWER IS THE ACCOUNT, AND IT HAS CHANGED. Adding a first
            // password moves the Cognito user from EXTERNAL_PROVIDER to
            // CONFIRMED, so googleAuthenticated comes back false -- and the
            // section un-greys and loses its "(Google authenticated)" caption
            // because there really is a password now. Without this the screen
            // would keep offering to add one until a reload.
            if ( aSaved ) setAccount( aSaved );

            setPasswordSaved( aGoogleOnly
                ? t( 'account-settings-password-added', 'Your password has been added. You can now sign in with it or with Google.' )
                : t( 'account-settings-password-changed', 'Your password has been changed.' ) );
        }
        catch ( aError ) {
            setPasswordError( aError.message );
        }
    };

    const inputClass = 'w-full border border-field-edge bg-field rounded-md shadow-one'
        + ' dark:shadow-register py-3 px-6 text-body text-base placeholder-placeholder outline-hidden'
        + ' focus-visible:shadow-none focus:border-focus';

    // A Google account has no Cognito password -- so there is no CURRENT one to
    // type, and that one box is greyed. The other two are not: since 2026-09-22
    // this form ADDS a first password to such an account, after which they can
    // sign in either way and the Google identity still works.
    //
    // Greyed rather than hidden, which was already the rule here and matters
    // more now: a box that vanishes reads as a missing feature, and one that is
    // visible, dead and captioned reads as an answer to "why am I not being
    // asked for my current password".
    const aGoogleOnly = Boolean( account && account.googleAuthenticated );

    // ⚠️ THIS IS A LABEL, NOT A PERMISSION. esback asks Cognito whether there is
    // a password to check and ignores the body on that question entirely -- see
    // EsAccountController.changePassword. Nothing here is what lets the current
    // password be skipped.
    const currentPasswordClass = aGoogleOnly
        ? inputClass + ' cursor-not-allowed opacity-50'
        : inputClass;

    // NO ROW, NO FORM. Everything below this point edits an account, and there
    // is not one -- so the nickname box, the Save it could never complete and
    // the whole password section are not drawn at all rather than drawn dead.
    //
    // It keeps es-account-page so the app shell, the nav and any step that
    // simply asks whether the account route resolved still find what they
    // expect; what is missing is the form, which is the honest difference.
    if ( notFound ) {
        return (
            <section data-testid="es-account-page" className="relative z-10 pb-4 pt-nav-room">
                <div className="container">
                    <div className="flex flex-wrap mx-[-16px]">
                        <div className="w-full px-4">
                            <div className="mx-auto rounded-md p-12 max-w-[500px] sm:p-[60px]">
                                <h3 data-testid="es-account-heading" className="mb-3 text-center text-2xl font-bold text-heading sm:text-3xl">
                                    { t( 'account-settings-not-found-heading', 'User not found' ) }
                                </h3>
                                { /* Says what is true and what to do, and does
                                     not guess at why. The account may never have
                                     finished registering, or may have been
                                     removed while the session stayed valid --
                                     this screen cannot tell those apart and
                                     should not pretend to. */ }
                                <p data-testid="es-account-not-found" className="formSubHeader">
                                    { t( 'account-settings-not-found-text', 'You are logged in, but there is no Escapade account for this login. Registering again will make one.' ) }
                                </p>
                                <p className="mt-8 text-center text-base">
                                    <a href={ VIEW_REGISTER } data-testid="es-account-not-found-register-link"
                                       className="text-accent hover:underline">
                                        { t( 'account-settings-not-found-register', 'Register' ) }
                                    </a>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    // Three tabs. The tab rides in the address (?tab=), as the Packs page's does; a value
    // that is not a tab drawn here -- a flag that is off, a typo -- falls back to Personal.
    const personalPanel = (
        <>
            { /* The address, shown and not editable.

                 It is the IDENTITY after the email-as-identity
                 change, and changing it would mean changing the
                 Cognito user, the es_account row and every
                 table that holds the value as text -- which is
                 a migration, not a form field. It is here
                 because it is how you know which account you
                 are logged in as when the nickname says
                 something else.

                 Nobody else ever sees this. EsAccountView is
                 only ever answered for your own row or to an
                 admin; every screen that renders a name to
                 another player renders displayName. */ }
            <p data-testid="es-account-email" className="mb-8 text-center text-sm text-body">
                { t( 'account-settings-logged-in-as', 'Logged in as {email}', { email: account?.email || '…' } ) }
            </p>

            <form id="accountForm" data-testid="es-account-form" onSubmit={handleAccountSubmit}>
                <div className="mb-8">
                    <label className="mb-3 block text-sm font-medium text-heading" htmlFor="nickname">
                        {' '}{ t( 'account-settings-nickname', 'Nickname' ) }{' '}
                    </label>
                    <input autoComplete="off"
                        onChange={ ( event ) => {
                            setNicknameError( null );
                            setNickname( event.target.value );
                        } }
                        value={nickname}
                        type="text"
                        id="nickname"
                        name="nickname"
                        placeholder={ account?.displayName || '' }
                        data-testid="es-account-nickname-input"
                        className={inputClass} />
                    <p data-testid="es-account-nickname-rule" className="mt-2 text-xs text-aside">
                        { t( 'account-settings-nickname-rule', 'This is the only name other players see.' ) }
                    </p>
                    { nicknameError &&
                        <p data-testid="es-account-nickname-error" role="alert"
                           className="mt-2 text-xs text-danger">{ nicknameError }</p> }
                </div>
                { /* Unchecked is the default and is what every account
                     already is -- not having opted out is what being
                     opted in means, so there is no third state and
                     nothing to decide on first load.

                     It is a PLAIN input rather than EsCheckbox: this
                     page is marketing chrome built on the template's
                     own form classes and imports nothing from EsPrime,
                     and one PrimeReact part in the middle of it would
                     be the only themed control on the screen.

                     The label wraps the box so the WORDS are clickable
                     too, which is most of the target. */ }
                <div className="mb-6">
                    <label htmlFor="isOptOut"
                           className="flex cursor-pointer items-center gap-3 text-base text-body">
                        <input
                            type="checkbox"
                            id="isOptOut"
                            name="isOptOut"
                            data-testid="es-account-opt-out-input"
                            checked={ optOut }
                            onChange={ ( pEvent ) => setOptOut( pEvent.target.checked ) }
                            className="size-4 shrink-0 accent-accent" />
                        { t( 'account-settings-opt-out', 'Opt-out from Escapade mailings' ) }
                    </label>
                    <p data-testid="es-account-opt-out-rule" className="mt-2 text-xs text-aside">
                        { /* What it does NOT cover, which is the question a
                             reader actually has -- somebody who ticks this
                             and then stops getting a password reset would
                             have been misled by a box that only said "no
                             mailings".

                             It opened with "Announcements and news only.",
                             cut by Tim on 2026-09-18: the checkbox beside
                             it already says what it turns off, and a helper
                             line that restates the label is a line people
                             stop reading. */ }
                        { t( 'account-settings-opt-out-rule', 'You will still get anything about your own account.' ) }
                    </p>
                </div>
                { /* Initials and avatar on one row (Tim, 2026-10-09): the two
                     are the same thing, what a character shows for you, and
                     each is a short field. Wraps on a phone. */ }
                <div className="mb-8 flex flex-wrap gap-8">
                <div className="min-w-48 flex-1">
                    <label className="mb-3 block text-sm font-medium text-heading" htmlFor="initials">
                        { t( 'account-settings-initials', 'Initials' ) }
                    </label>
                    { /* Three letters want a three-letter box, not the row's width. */ }
                    <div className="w-24">
                        <input
                            onChange={ ( event ) => setInitials( event.target.value.toUpperCase() ) }
                            value={ initials }
                            type="text"
                            id="initials"
                            name="initials"
                            maxLength={ 3 }
                            autoComplete="off"
                            placeholder={ account?.shownInitials || '' }
                            data-testid="es-account-initials-input"
                            className={inputClass} />
                    </div>
                    <p data-testid="es-account-initials-rule" className="mt-2 text-xs text-aside">
                        { t( 'account-settings-initials-rule', 'One to three letters.' ) }
                    </p>
                </div>
                <div className="flex-1">
                    <p className="mb-3 block text-sm font-medium text-heading">{ t( 'account-settings-avatar', 'Avatar' ) }</p>
                    <div className="flex items-center gap-3">
                        { account?.avatarRev &&
                            <img data-testid="es-account-avatar"
                                 src={ serverUrl + API_ACCOUNT_AVATAR + '?v=' + account.avatarRev }
                                 alt={ t( 'account-settings-avatar-alt', 'Your avatar' ) } className="size-14 rounded-full object-cover" /> }
                        { !account?.avatarRev && account?.pictureUrl &&
                            <img data-testid="es-account-avatar-google"
                                 src={ account.pictureUrl } referrerPolicy="no-referrer"
                                 alt={ t( 'account-settings-avatar-google-alt', 'Your Google avatar' ) } className="size-14 rounded-full object-cover" /> }
                        <EsFileUpload data-testid="es-account-avatar-upload"
                                      accept="image/png,image/jpeg,image/webp"
                                      disabled={ !account || avatarBusy }
                                      size="small" severity="secondary" variant="outlined"
                                      label={ account?.avatarRev ? t( 'account-settings-avatar-change', 'Change' ) : t( 'account-settings-avatar-upload', 'Upload' ) }
                                      onFile={ uploadAvatar } />
                        { account?.avatarRev &&
                            <button type="button" data-testid="es-account-avatar-remove"
                                    disabled={ avatarBusy }
                                    onClick={ () => setAvatarConfirming( true ) }
                                    className="text-sm text-danger hover:underline disabled:opacity-50">
                                { t( 'account-settings-avatar-remove', 'Remove' ) }
                            </button> }
                    </div>
                    { avatarNote &&
                        <p data-testid="es-account-avatar-error" role="alert"
                           className="mt-2 text-xs text-danger">{ avatarNote }</p> }
                </div>
                </div>
                { localeOn &&
                    <div className="mb-6">
                        <p className="mb-3 block text-sm font-medium text-heading">{ t( 'account-settings-language', 'Language' ) }</p>
                        <EsLocalePicker />
                    </div> }
                { copykeysOn && account &&
                    <div className="mb-6">
                        <p className="mb-3 block text-sm font-medium text-heading">{ t( 'account-settings-translate', 'I can translate' ) }</p>
                        <EsTranslateOffer offer={ account.translateOffer } />
                    </div> }
                { voiceOn && account &&
                    <div className="mb-6">
                        <p className="mb-3 block text-sm font-medium text-heading">{ t( 'account-settings-voice', 'Table voice' ) }</p>
                        <EsAccountVoice />
                    </div> }
                { /* Under 18 sees no AI control at all, not a disabled one (es-ai-service.md §5). */ }
                { aiOn && account && account.aiEligible &&
                    <div className="mb-6">
                        <p className="mb-3 block text-sm font-medium text-heading">{ t( 'ai-opt-in-heading', 'AI assistant' ) }</p>
                        <EsAccountAi optIn={ account.aiOptIn }
                                     onChange={ ( pOptIn ) => setAccount( { ...account, aiOptIn: pOptIn } ) } />
                        { account.aiOptIn && <EsAiMeter className="mt-2 text-xs text-aside" data-testid="es-account-ai-meter" /> }
                    </div> }
                { /* THE BUTTON IS THE STATUS, since 2026-09-18.
                     There was a "Saved." line under it as well, so a
                     successful save left a green Save button inviting
                     a press it would do nothing with, and a sentence
                     underneath saying the press had already happened.
                     Two controls reporting one fact, disagreeing.

                     Saved and clean: it reads "Saved" and is greyed,
                     which says there is nothing to do rather than
                     saying it twice. Touch any field and it is "Save"
                     and live again -- see dirty. */ }
                <div className="mb-6">
                    <button
                        type="submit"
                        disabled={ !account || !dirty }
                        data-testid="es-account-save-button"
                        className="buttonSubmit flex w-full items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed">
                        { !dirty && accountSaved ? t( 'account-settings-saved', 'Saved' ) : t( 'account-settings-save', 'Save' ) }
                    </button>
                </div>
            </form>

            { /* The error still gets a line of its own. A failed save
                 has something to SAY -- which field, or what the server
                 refused -- and a button cannot carry a sentence. */ }
            { accountError &&
                <p ref={accountErrorRef} data-testid="es-account-error" className="formError"> {accountError} </p> }

            <EsAccountBridge />

            { /* Last on the tab and set apart by its color alone; the button only opens
                 the typed confirm. */ }
            <div className="mt-8" data-testid="es-account-delete">
                <h4 className="mb-2 text-lg font-bold text-heading" data-testid="es-account-delete-heading">
                    { t( 'account-settings-delete-heading', 'Delete account' ) }
                </h4>
                <p className="mb-4 text-sm text-body" data-testid="es-account-delete-blurb">
                    { t( 'account-settings-delete-blurb', 'Delete this account and everything it owns. There is no undo.' ) }
                </p>
                <button type="button"
                        data-testid="es-account-delete-button"
                        disabled={ !account || deleteBusy }
                        onClick={ askToDelete }
                        className="accountDeleteButton">
                    { t( 'account-settings-delete-button', 'Delete account' ) }
                </button>
                { deleteError &&
                    <p data-testid="es-account-delete-error" role="alert" className="formError">{ deleteError }</p> }
            </div>

            <EsConfirmDelete open={ forkedAsking }
                             data-testid="es-account-delete-forked"
                             title={ t( 'account-settings-delete-dialog-forked-title', 'Other accounts built on your packs' ) }
                             what={ t( 'account-settings-delete-dialog-forked-what', 'Deleting your account hides these packs, and the packs other accounts built on them leave the Library. Those accounts keep their packs.' ) }
                             confirm={ t( 'account-settings-delete-dialog-forked-confirm', 'Delete anyway' ) }
                             onCancel={ () => setForkedAsking( false ) }
                             onConfirm={ deleteAnyway }>
                <EsForkedPacks packs={ forked } data-testid="es-account-delete-forked-packs" />
            </EsConfirmDelete>

            <EsConfirmDelete open={ deleteAsking }
                             data-testid="es-account-delete-dialog"
                             title={ t( 'account-settings-delete-dialog-title', 'Delete your account?' ) }
                             what={ t( 'account-settings-delete-dialog-what', 'Your characters, notes, packs, and colorsheets are deleted, along with your subscription and any time left on it. A campaign you own passes to another GM or player at the table; one nobody else sits at is deleted with everything in it. This cannot be undone.' ) }
                             word={ account?.email || '' }
                             prompt={ t( 'account-settings-delete-dialog-prompt', 'Type your account email address to confirm' ) }
                             confirm={ t( 'account-settings-delete-dialog-confirm', 'Delete account' ) }
                             busy={ deleteBusy }
                             onTyped={ setDeleteTyped }
                             onCancel={ () => setDeleteAsking( false ) }
                             onConfirm={ deleteAccount } />

            <EsConfirmDelete open={ avatarConfirming }
                             data-testid="es-account-avatar-dialog"
                             title={ t( 'account-settings-avatar-dialog-title', 'Remove your avatar?' ) }
                             what={ t( 'account-settings-avatar-dialog-what', 'Your avatar picture is deleted. Your account button and any character without a picture of its own show your Google picture, if you sign in with Google, else your initials.' ) }
                             confirm={ t( 'account-settings-avatar-dialog-confirm', 'Remove' ) }
                             busy={ avatarBusy }
                             onCancel={ () => setAvatarConfirming( false ) }
                             onConfirm={ removeAvatar } />
        </>
    );

    const passwordPanel = (
        <>
            { aGoogleOnly &&
                <p data-testid="es-account-password-google" className="mb-8 text-center text-sm text-muted">
                    { t( 'account-settings-password-google', '(Google authenticated)' ) }
                </p> }

            <form id="passwordForm" data-testid="es-account-password-form" onSubmit={handlePasswordSubmit}>
                { /* The current password, and it is not a
                     formality. Cognito's AdminSetUserPassword
                     needs no old password, so without this box
                     a session somebody else picked up would be
                     enough to take the account -- a stolen
                     session expires, a changed password does
                     not. esback checks it by logging in with
                     it; see EsAccountService.passwordMatches. */ }
                <div className="mb-8">
                    <label className="mb-3 block text-sm font-medium text-heading" htmlFor="currentPassword">
                        {' '}{ t( 'account-settings-password-current', 'Current Password' ) }{' '}
                    </label>
                    <input
                        onChange={handlePasswordChange}
                        value={passwords.currentPassword}
                        type="password"
                        id="currentPassword"
                        name="currentPassword"
                        autoComplete="current-password"
                        data-testid="es-account-current-password-input"
                        disabled={aGoogleOnly}
                        className={currentPasswordClass} />
                    { /* Says why the box above is dead. Without it a greyed field
                         over two live ones reads as something broken. */ }
                    { aGoogleOnly &&
                        <p data-testid="es-account-password-add-note" className="mt-2 text-sm text-muted">
                            { t( 'account-settings-password-add-note', 'You logged in with Google, so there is no current password to type. Choose a new one below and you\'ll be able to login either way.' ) }
                        </p> }
                </div>
                <div className="mb-8">
                    <label className="mb-3 block text-sm font-medium text-heading" htmlFor="newPassword">
                        {' '}{ t( 'account-settings-password-new', 'New Password' ) }{' '}
                    </label>
                    <input
                        onChange={handlePasswordChange}
                        value={passwords.newPassword}
                        type="password"
                        id="newPassword"
                        name="newPassword"
                        autoComplete="new-password"
                        data-testid="es-account-new-password-input"
                        className={inputClass} />
                    <ul data-testid="es-account-password-rule" className="mt-2 list-disc ps-5 text-xs text-aside">
                        <li>{ t( 'account-settings-password-rule-length', 'At least 10 characters' ) }</li>
                        <li>{ t( 'account-settings-password-rule-upper', 'An uppercase letter' ) }</li>
                        <li>{ t( 'account-settings-password-rule-lower', 'A lowercase letter' ) }</li>
                        <li>{ t( 'account-settings-password-rule-number', 'A number' ) }</li>
                        <li>{ t( 'account-settings-password-rule-symbol', 'A symbol' ) }</li>
                    </ul>
                </div>
                <div className="mb-8">
                    <label className="mb-3 block text-sm font-medium text-heading" htmlFor="checkPassword">
                        {' '}{ t( 'account-settings-password-check', 'Check Password' ) }{' '}
                    </label>
                    <input
                        onChange={handlePasswordChange}
                        value={passwords.checkPassword}
                        type="password"
                        id="checkPassword"
                        name="checkPassword"
                        autoComplete="new-password"
                        data-testid="es-account-check-password-input"
                        className={inputClass} />
                </div>
                <div className="mb-6">
                    <button
                        type="submit"
                        data-testid="es-account-password-button"
                        className="buttonSubmit flex w-full items-center justify-center">
                        { aGoogleOnly ? t( 'account-settings-password-add', 'Add password' ) : t( 'account-settings-password-change', 'Change password' ) }
                    </button>
                </div>
            </form>

            { passwordError
                ? <p ref={passwordErrorRef} data-testid="es-account-password-error" className="formError"> {passwordError} </p>
                : passwordSaved
                    ? <p data-testid="es-account-password-saved" className="formStatus"> {passwordSaved} </p>
                    : null }
        </>
    );

    const tabs = [
        { value: TAB_PERSONAL, title: t( 'account-settings-tab-personal', 'Personal' ), content: personalPanel },
        { value: TAB_PASSWORD, title: t( 'account-settings-tab-password', 'Password' ), content: passwordPanel },
        { value: TAB_SUBSCRIPTION, title: t( 'account-settings-tab-subscription', 'Subscription' ), content: <EsAccountSub /> },
        ...( devicesOn ? [ { value: TAB_DEVICES, title: t( 'account-settings-tab-devices', 'Devices' ), content: <EsAccountDevices active={ search.get( 'tab' ) === TAB_DEVICES } /> } ] : [] ),
        // The games this account registered for the plugin (§6.1), under the same flag.
        ...( devicesOn ? [ { value: TAB_CLIENTS, title: t( 'account-settings-tab-clients', 'Game clients' ), content: <EsAccountClients /> } ] : [] ),
    ];
    const aTab = tabs.some( ( pTab ) => pTab.value === search.get( 'tab' ) ) ? search.get( 'tab' ) : TAB_PERSONAL;

    return (
        <section data-testid="es-account-page" className="relative z-10 pb-4 pt-nav-room">
            <EsMinis />
            <div className="container">
                <div className="flex flex-wrap mx-[-16px]">
                    <div className="w-full px-4">
                        <div className="mx-auto rounded-md px-12 pb-12 pt-6 max-w-[640px] sm:px-[60px] sm:pb-[60px] sm:pt-6">

                            <h3 data-testid="es-account-heading" className="mb-6 text-center text-2xl font-bold text-heading sm:text-3xl">
                                { t( 'account-settings-heading', 'Your account' ) }
                            </h3>

                            <EsTabs value={ aTab } onChange={ ( pTab ) => setSearch( { tab: pTab }, { replace: true } ) }
                                    className="packTabs accountTabs" listClassName="packTabList"
                                    tabs={ tabs } data-testid="es-account" />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
